import HiddenInput from "@/components/form/components/HiddenInput";
import { setErrorsToForm } from "@/utils/formUtils";
import { Form, Modal } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateContractGainers } from "../../../common/api/queries";
import { ModalSizes } from "../../../common/constants";
import { hasPermission } from "../../../common/security/utils";
import { useCurrentAgentAccount } from "../../auth/hooks";
import { ContractType } from "../enums";
import { useContractValidation } from "../hooks";
import { Contract, UpdateContractGainers } from "../types";
import {
  contractPermissionsByContractType,
  convertGainerRecordsToCreateUpdateObject,
  isContractVerified,
} from "../utils";
import { ContractGainersFormSection } from "./forms/ContractGainersFormSection";

type Props = {
  isOpen: boolean;
  onCancel: () => void;
  contract: Contract;
};

export const ContractUpdateGainersModal = ({ isOpen, onCancel, contract }: Props) => {
  const { t } = useTranslation();

  const userAccount = useCurrentAgentAccount();

  const updateContractGainers = useUpdateContractGainers();

  const [form] = Form.useForm<UpdateContractGainers & { clientIdentifiers: string[]; type: ContractType }>();

  const handleContractValidationChange = useContractValidation({ form, contractId: contract.id });

  useEffect(() => {
    if (isOpen) {
      form.setFieldsValue({
        type: contract.type,
        clientIdentifiers: contract.clients.map((client) => client.identifier),
        gainerRecords: convertGainerRecordsToCreateUpdateObject(contract.gainerRecords ?? []),
        optimisticLockVersion: contract.optimisticLockVersion,
      });
    }
  }, [isOpen, contract]);

  const disablePrivilegedInput =
    isContractVerified(contract) &&
    userAccount &&
    !hasPermission(
      userAccount.permissions,
      contractPermissionsByContractType[contract.type].privilegedChangesOnVerified,
    );

  const handleSubmit = () => {
    form
      .validateFields()
      .then(({ clientIdentifiers, ...values }) =>
        updateContractGainers.mutate(
          { id: contract.id, object: values },
          { onSuccess: onCancel, onError: (error) => setErrorsToForm(form, "contract.attrs", error.violations) },
        ),
      );
  };

  return (
    <Modal
      open={isOpen}
      width={ModalSizes.XXL}
      title={t("contract.titles.updateGainers")}
      confirmLoading={updateContractGainers.isPending}
      onOk={handleSubmit}
      onCancel={onCancel}
      afterClose={() => form.resetFields()}
      mask={{ closable: false }}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
    >
      <Form
        form={form}
        layout="vertical"
        name="updateContractGainersForm"
        onValuesChange={handleContractValidationChange}
      >
        <HiddenInput name="type" />
        <HiddenInput name="clientIdentifiers" />
        <HiddenInput name="optimisticLockVersion" initialValue={contract.optimisticLockVersion} />

        <ContractGainersFormSection initialContract={contract} disablePrivilegedInput={!!disablePrivilegedInput} />
      </Form>
    </Modal>
  );
};
